import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";
import {
  Animated,
  Platform,
  StyleSheet,
  useWindowDimensions,
} from "react-native";
import { Logo } from "~/features/logo/Logo";
import { styleConstants } from "~/style/styleConstants";
import { Collapsible } from "./Collapsible";
import { Text } from "./Text";
import { View } from "./View";

export function WithSplashScreen({
  children,
  isAppReady,
}: {
  isAppReady: boolean;
  children: React.ReactNode;
}) {
  return (
    <>
      {isAppReady && children}

      {Platform.OS !== "web" && <Splash isAppReady={isAppReady} />}
    </>
  );
}

// Animation constants
const fadeInDuration = __DEV__ ? 1 : 150;
const fadeOutDuration = __DEV__ ? 1 : 250;

const FADE_IN_IMAGE = "Fade in image";
const WAIT_FOR_APP_TO_BE_READY = "Wait for app to be ready";
const FADE_OUT = "Fade out";
const HIDDEN = "Hidden";

export const splashScreenState = observable({
  message: "",
});

export const Splash = observer(function Splash({
  isAppReady,
}: {
  isAppReady: boolean;
}) {
  const window = useWindowDimensions();

  const containerOpacity = useRef(new Animated.Value(1)).current;
  const lottieOpacity = useRef(new Animated.Value(0)).current;

  const [state, setState] = useState<
    | typeof FADE_IN_IMAGE
    | typeof WAIT_FOR_APP_TO_BE_READY
    | typeof FADE_OUT
    | typeof HIDDEN
  >(FADE_IN_IMAGE);

  useEffect(() => {
    if (state === FADE_IN_IMAGE) {
      Animated.timing(lottieOpacity, {
        toValue: 1,
        duration: fadeInDuration, // Fade in duration
        useNativeDriver: true,
      }).start(() => {
        setState(WAIT_FOR_APP_TO_BE_READY);
      });
    }
  }, [lottieOpacity, state]);

  useEffect(() => {
    if (state === WAIT_FOR_APP_TO_BE_READY) {
      if (isAppReady) {
        setState(FADE_OUT);
      }
    }
  }, [isAppReady, state]);

  useEffect(() => {
    if (state === FADE_OUT) {
      Animated.timing(containerOpacity, {
        toValue: 0,
        duration: fadeOutDuration, // Fade out duration
        delay: 0, // Minimum time the logo will stay visible (0 = only while app is initialized)
        useNativeDriver: true,
      }).start(() => {
        setState(HIDDEN);
      });
    }
  }, [containerOpacity, state]);

  if (state === HIDDEN) return null;

  return (
    <Animated.View
      collapsable={false}
      style={[style.container, { opacity: containerOpacity }]}
    >
      <Animated.View style={[style.lottieWrap, { opacity: lottieOpacity }]}>
        <View style={style.iconContainer}>
          <Logo
            size={window.width * 0.38}
            backgroundColor="transparent"
            strokeColor={styleConstants.colorBackgroundWhite}
          />
        </View>
      </Animated.View>

      <Collapsible collapsed={!splashScreenState.message}>
        <Text weightSemiBold style={{ height: 64 }} colorDarkSoft alignCenter>
          {splashScreenState.message}
        </Text>
      </Collapsible>
    </Animated.View>
  );
});

const style = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: styleConstants.colorBackgroundTheme,
    alignItems: "center",
    justifyContent: "center",
  },
  lottieWrap: {
    width: 250,
    height: 250,
  },
  iconContainer: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
});
